.stickySentinel {
  /* Marker just above the header in flow — CSS has no way to ask "is this sticky
     element currently stuck", so Header.jsx watches this via IntersectionObserver
     instead: once it scrolls out of view, the header has left its normal position and
     is sitting at its sticky offset, which is when `.stuck` below kicks in. 1px, not 0 —
     a zero-area element has no intersection area to report as "intersecting" again once
     it leaves the viewport, so it would get stuck reporting false forever. */
  height: 1px;
  margin-bottom: -1px;
}
.header {
  /* Floating rounded card that stays pinned to the top of the viewport while scrolling
     (position: sticky). It's in normal flow, which would push the hero down below it —
     the hero cancels that reserved space with a matching negative margin (Hero.module.css)
     so the video still runs full-bleed from the top, with the header floating over it. */
  position: sticky;
  top: var(--header-margin);
  margin: var(--header-margin);
  z-index: 50;
  border-radius: 10px;
  /* Dark glass: translucent asphalt plus a blur, so the hero video reads through it
     without the nav text ever losing contrast. */
  background: rgba(1, 8, 15, 0.78);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  backdrop-filter: blur(14px) saturate(140%);
  box-shadow: var(--shadow-md);
  border: 1px solid rgb(152 152 152 / 33%);
  transition: top var(--dur) var(--ease);
}
.stuck {
  /* Settles 15px higher than its resting sticky offset once actually pinned, so it
     visibly snaps into place instead of just appearing there. */
  top: calc(var(--header-margin) - 15px);
  border: 1px solid rgb(152 152 152 / 33%);
}
.bar {
  display: flex;
  align-items: center;
  gap: var(--sp-5);
  min-height: var(--header-h);
  margin-inline: auto;
  padding-inline: var(--sp-6);
}
.logo {
  /* The nav gives up space first; a shrinking logo gets squashed out of proportion. */
  flex-shrink: 0;
}
.logo img {
  height: 1.75rem;
  width: auto;
}
.nav {
  display: none;
  flex: 1;
}
.groups {
  display: flex;
  gap: var(--sp-header);
}
.groupButton {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  padding: var(--sp-2) var(--sp-3);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  font-family: var(--font-display);
  /* One size and weight for everything sitting in the bar: triggers, login, demo. */
  font-size: 13.5px;
  font-weight: 400;
  line-height: 1;
  color: var(--text-strong);
  white-space: nowrap;
}
.groupButton:hover {
  background: var(--surface-hover);
}
.groupButton[aria-expanded="true"] {
  background: var(--surface-hover);
  /* Inset outline rather than a border, so opening a tab never shifts the nav. */
  box-shadow: inset 0 0 0 1px var(--border-strong);
}
.groupButton[aria-expanded="true"]::after {
  /* A short dash under the open tab — a lane marking, in the accent. */
  content: "";
  position: absolute;
  left: 50%;
  bottom: -6px;
  width: 1rem;
  height: 2px;
  border-radius: 1px;
  background: var(--accent);
  transform: translateX(-50%);
}
.groupButton[aria-expanded="true"] .chevron {
  transform: translateY(2px) rotate(-135deg);
}
.chevron {
  width: 0.4rem;
  height: 0.4rem;
  border-right: 1px solid currentColor;
  border-bottom: 1px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
  transition: transform var(--dur) var(--ease);
}

/* Mega menu. Positioned against the whole header card (the nearest positioned
   ancestor — the <li>s are deliberately static), so every panel opens from the header's
   left edge at the same width, whichever tab opened it. */
.menu {
  position: absolute;
  top: calc(100% + var(--sp-2));
  left: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 21rem;
  width: min(100%, 62rem);
  /* No overflow: hidden here — it would clip the ::before gap bridge above the panel,
     and clipped areas can't be hovered. The zones round their own corners instead. */
  background: var(--surface-raised);
  border: 1px solid #788290;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  /* Opaque, not glass: a backdrop-filter nested inside the header's own filter can
     only blur what's inside the header, never the page behind it. */
  animation: menuIn 180ms var(--ease);
}
.menu::before {
  /* Bridges the gap below the header card, so crossing it doesn't count as leaving. */
  content: "";
  position: absolute;
  inset: calc(-1 * var(--sp-2) - 1px) 0 auto;
  height: calc(var(--sp-2) + 1px);
}
@keyframes menuIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
}
.menuLinks {
  padding: var(--sp-6);
  border-radius: calc(var(--radius-lg) - 1px) 0 0 calc(var(--radius-lg) - 1px);
  background: var(--surface-alt);
}
.menuEyebrow,
.featureEyebrow {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}
.menuList {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-1) var(--sp-4);
  margin-top: var(--sp-4);
}
.menuLink {
  display: grid;
  gap: 2px;
  padding: var(--sp-3);
  border-radius: var(--radius);
  text-decoration: none;
  color: var(--text-strong);
}
.menuLink:hover,
.menuLink:focus-visible {
  background: var(--surface-hover);
  color: var(--text-strong);
}
.menuLink[aria-current="page"] .menuLabel {
  color: var(--accent);
}
.menuLabel {
  font-size: 15px;
  font-weight: 600;
}
.menuBlurb {
  font-size: 13px;
  line-height: 1.45;
  color: var(--text-muted);
}
.feature {
  display: grid;
  align-content: start;
  gap: var(--sp-2);
  padding: var(--sp-6);
  text-decoration: none;
  color: var(--text-strong);
}
.featureImage {
  margin-bottom: var(--sp-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.featureLogos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  place-items: center;
  gap: var(--sp-4) var(--sp-3);
  aspect-ratio: 16 / 9;
  margin-bottom: var(--sp-2);
  padding: var(--sp-4);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-alt);
}
.featureLogos img {
  width: auto;
  height: 1.9rem;
  max-width: 100%;
  object-fit: contain;
  opacity: 0.8;
}
.featureTitle {
  font-size: var(--fs-lg);
  font-weight: 600;
  line-height: var(--lh-snug);
  letter-spacing: -0.01em;
}
.featureCta {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-1);
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
}
.featureArrow {
  width: 0.4rem;
  height: 0.4rem;
  border-top: 1.5px solid currentColor;
  border-right: 1.5px solid currentColor;
  transform: rotate(45deg);
  transition: transform var(--dur) var(--ease);
}
.feature:hover {
  /* Beats reset.css's `a:hover` so only the CTA line lights up, not the whole card. */
  color: var(--text-strong);
}
.feature:hover .featureCta,
.feature:focus-visible .featureCta {
  color: var(--accent-hover);
}
.feature:hover .featureArrow,
.feature:focus-visible .featureArrow {
  transform: translateX(3px) rotate(45deg);
}
.actions {
  display: flex;
  align-items: center;
  gap: var(--sp-5);
  margin-left: auto;
}
.login {
  display: none;
  font-size: 13.5px;
  font-weight: 400;
  line-height: normal;
  color: var(--text-strong);
  text-decoration: none;
  white-space: nowrap;
}
.loginIcon {
  /* Sized in em against the label, at roughly the cap height of the "L" beside it — in
     rem it stayed 12px while the capital is ~9.7px, so the figure stood taller than the
     word it belongs to. */
  position: relative;
  display: inline-block;
  width: 0.72em;
  height: 0.72em;
  flex-shrink: 0;
}
.loginIcon::before {
  /* head */
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0.3em;
  height: 0.3em;
  border-radius: 50%;
  background: currentColor;
}
.loginIcon::after {
  /* shoulders */
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0.72em;
  height: 0.36em;
  border-radius: 0.5em 0.5em 0 0;
  background: currentColor;
}
.login:hover {
  color: var(--link-hover);
}
.demo {
  /* A lit outline rather than a solid block: the bar is dark glass, and a filled button
     sits on top of it like a sticker. The accent lives in the rule and the text, and only
     floods the pill on hover — so the invitation is still obvious without shouting. */
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.95rem;
  border: 1px solid color-mix(in srgb, var(--accent) 55%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent) 12%, transparent);
  color: var(--orange-400);
  font-size: 13.5px;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background-color var(--dur) var(--ease),
    border-color var(--dur) var(--ease),
    color var(--dur) var(--ease);
}
.demo:hover {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--text-on-accent);
}

.burger {
  display: inline-grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  background: none;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}
.burgerIcon,
.burgerIcon::before,
.burgerIcon::after {
  display: block;
  width: 1.1rem;
  height: 2px;
  background: var(--text-strong);
  border-radius: 1px;
}
.burgerIcon {
  position: relative;
}
.burgerIcon::before,
.burgerIcon::after {
  content: "";
  position: absolute;
  left: 0;
}
.burgerIcon::before {
  top: -5px;
}
.burgerIcon::after {
  top: 5px;
}
/* The full nav needs ~1230px even with the narrow gap: from 64rem it pushed "Request a demo"
   off the right edge of every 1024–1230px screen (landscape tablets, small laptops). */
@media (min-width: 80rem) {
  .nav {
    display: block;
  }
  .burger {
    display: none;
  }
  .logo img {
    height: 1.75rem;
  }
}
@media (min-width: 48rem) {
  /* "Login" is short enough to sit in the bar well before the nav collapses. */
  .login {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-2);
  }
}
@media (max-width: 29.99rem) {
  /* The narrowest phones: logo and menu button only. */
  .demo {
    display: none;
  }
}
/* With the wide gap the bar needs ~1320px; at 80rem it overran by ~40px. */
@media (min-width: 85rem) {
  .bar {
    gap: var(--sp-16);
  }
}
